import { ArrowRight, Sparkles, Zap, Package } from 'lucide-react';
import { Badge } from '@neurograph/primitives';
import { useStudio } from '../providers/StudioContext';
import { BriefSectionEditor } from '../components/BriefSectionEditor';
import { BRIEF_SECTION_KEYS, BRIEF_SECTION_LABELS } from '../types';
import type { BriefSectionKey, BriefSection } from '../types';

export function BriefPanel() {
  const { brief, personas, setActivePanel, updateBrief, loading } = useStudio();

  const handleSaveSection = async (sectionKey: BriefSectionKey, value: BriefSection) => {
    await updateBrief({ [sectionKey]: value });
  };

  if (!brief) {
    return (
      <div className="text-center py-16 px-8">
        <h3 className="text-lg font-semibold text-neutral-700 mb-2">No Brief Loaded</h3>
        <p className="text-neutral-500 mb-6">
          Create a brief or select an existing one to get started.
        </p>
        {personas.length > 0 && (
          <div className="mt-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-neutral-400 mb-3">
              Available Personas
            </p>
            <div className="flex flex-wrap gap-2 justify-center">
              {personas.map((p) => (
                <span
                  key={p.id || p.persona_id || p.name}
                  className="px-3 py-1.5 bg-primary-50 text-primary-700 rounded-full text-sm"
                >
                  {p.name || p.persona_id || p.id}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Brief header */}
      <div className="flex items-start justify-between pb-4 border-b border-neutral-200">
        <div>
          <h2 className="text-xl font-bold text-neutral-900">
            {brief.name || 'Untitled Brief'}
          </h2>
          <div className="flex items-center gap-3 mt-1.5 text-sm text-neutral-500">
            {brief.persona_id && <span>Persona: {brief.persona_id}</span>}
            {brief.status && (
              <Badge variant={brief.status === 'active' ? 'success' : 'neutral'}>
                {brief.status}
              </Badge>
            )}
            {brief.version !== undefined && <span>v{brief.version}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setActivePanel('angles')}
            className="flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors"
          >
            <Sparkles size={14} />
            Generate Hooks & Angles
            <ArrowRight size={14} />
          </button>
          <button
            onClick={() => setActivePanel('assets')}
            className="flex items-center gap-1.5 px-4 py-2 bg-emerald-500 text-white rounded-lg text-sm font-semibold hover:bg-emerald-600 transition-colors"
            title="Skip directly to ad pack generation"
          >
            <Zap size={14} />
            Quick Ad Pack
          </button>
        </div>
      </div>

      {/* Quick Actions Card */}
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-4">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center flex-shrink-0">
            <Package size={18} className="text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-neutral-900 mb-1">Ready to Generate?</h3>
            <p className="text-sm text-neutral-600 mb-3">
              Choose your path: refine hooks & angles for precise creative control, or jump straight to ad pack generation for fast results.
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setActivePanel('angles')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary-700 bg-white border border-primary-200 rounded-lg hover:bg-primary-50 transition-colors"
              >
                <Sparkles size={12} />
                Hooks & Angles (Recommended)
              </button>
              <button
                onClick={() => setActivePanel('script')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-neutral-600 bg-white border border-neutral-200 rounded-lg hover:bg-neutral-50 transition-colors"
              >
                Scripts
              </button>
              <button
                onClick={() => setActivePanel('assets')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-700 bg-white border border-emerald-200 rounded-lg hover:bg-emerald-50 transition-colors"
              >
                <Zap size={12} />
                Direct to Ad Pack
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Brief sections */}
      {BRIEF_SECTION_KEYS.map((key) => (
        <BriefSectionEditor
          key={key}
          sectionKey={key}
          label={BRIEF_SECTION_LABELS[key]}
          data={brief[key] as BriefSection | undefined}
          onSave={handleSaveSection}
          isSaving={loading.brief}
        />
      ))}
    </div>
  );
}
